import { useState, useRef, useCallback } from 'react';

/**
 * Validates that all orders in game state have instanceIds
 * @param {Object} gameState - Game state to validate
 * @param {string} userId - User ID to validate for
 * @returns {boolean} - True if valid, throws error if invalid
 */
function validateGameStateInstanceIds(gameState, userId) {
  if (!gameState?.state?.[userId]) return true;
  
  const player = gameState.state[userId];
  let hasErrors = false;
  
  // Validate all orders in hand have instanceIds
  if (player.hand) {
    player.hand.forEach((order, idx) => {
      if (!order.instanceId) {
        console.error('Validation failed: Order missing instanceId in hand', {
          index: idx,
          orderName: order.name,
          orderId: order.id,
          type: order.type
        });
        hasErrors = true;
      }
    });
  }
  
  // Validate all units on battlefield have instanceIds
  ['front', 'flank'].forEach(zone => {
    if (player.playedOrders?.[zone]) {
      player.playedOrders[zone].forEach((unit, idx) => {
        if (unit && !unit.instanceId) {
          console.error('Validation failed: Unit missing instanceId on battlefield', {
            zone,
            index: idx,
            unitName: unit.name,
            unitId: unit.id
          });
          hasErrors = true;
        }
      });
    }
  });
  
  if (hasErrors) {
    throw new Error('Game state validation failed: Missing instanceIds detected. Check console for details.');
  }
  
  return true;
}

/**
 * Custom hook for managing game state synchronization
 * Handles state version checking, instanceId validation, and state stabilization
 * 
 * @param {string} userId - Current user ID
 * @param {Function} onStateUpdate - Callback when state is updated (for React state updates)
 * @returns {Object} - State sync utilities and flags
 */
export const useGameStateSync = (userId, onStateUpdate) => {
  // State refs - always have latest state (bypasses React render delay)
  const gameStateRef = useRef(null);
  const lastStateVersionRef = useRef(null);
  
  // State stabilization - prevents actions while React is rendering
  const stateStabilizationTimer = useRef(null);
  const [isStateStabilizing, setIsStateStabilizing] = useState(false);
  const STATE_STABILIZATION_DELAY_MS = 100; // Wait 100ms after state update for React to render
  
  // UI locking - prevents actions while waiting for backend response
  const [isStateUpdating, setIsStateUpdating] = useState(false);
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);
  const stateUpdateTimeout = useRef(null);
  const loadingOverlayTimeout = useRef(null);
  
  /**
   * Detects if instanceIds changed between current and incoming state
   * @param {Object} currentState - Current game state
   * @param {Object} incomingState - Incoming game state
   * @param {string} userId - User ID to check
   */
  const detectInstanceIdChanges = useCallback((currentState, incomingState, userId) => {
    const currentHand = currentState?.state?.[userId]?.hand || null;
    const incomingHand = incomingState?.state?.[userId]?.hand || null;
    if (!Array.isArray(currentHand) || !Array.isArray(incomingHand)) {
      return;
    }

    // Build a map from order ID -> array of instanceIds for both hands
    const buildIdToInstancesMap = (hand) => {
      const map = new Map();
      hand.forEach(order => {
        if (!order) return;
        const idKey = String(order.id);
        if (!map.has(idKey)) {
          map.set(idKey, []);
        }
        map.get(idKey).push(order.instanceId || null);
      });
      return map;
    };

    const currentMap = buildIdToInstancesMap(currentHand);
    const incomingMap = buildIdToInstancesMap(incomingHand);

    // Detect suspicious changes where the number of copies for an ID stays the same
    // but none of the instanceIds match between states (strong signal of mutation/re‑gen)
    currentMap.forEach((currentInstances, idKey) => {
      const incomingInstances = incomingMap.get(idKey) || [];

      // If the count changed, cards were added/removed; that's normal and not an instanceId mutation signal
      if (currentInstances.length !== incomingInstances.length) {
        return;
      }

      // Ignore IDs where we have no valid instanceIds to compare
      const currentValid = currentInstances.filter(Boolean);
      const incomingValid = incomingInstances.filter(Boolean);
      if (currentValid.length === 0 || incomingValid.length === 0) {
        return;
      }

      const incomingSet = new Set(incomingValid);
      const hasSharedInstance = currentValid.some(inst => incomingSet.has(inst));

      if (!hasSharedInstance) {
        console.error(`🚨 INSTANCEID CHANGED: Orders with ID ${idKey} have the same count in hand but completely different instanceIds between state updates.`, {
          currentInstances,
          incomingInstances
        });
        console.error('🚨 This strongly suggests instanceIds were regenerated or lost on backend for existing hand cards (same count, no shared instanceIds).');
      }
    });
  }, []);
  
  /**
   * Updates game state with validation and stabilization
   * @param {Object} incomingState - New game state from backend
   * @param {Object} options - Options for state update
   * @param {boolean} options.skipVersionCheck - Skip version check (for reconnect/desync recovery)
   * @param {boolean} options.skipValidation - Skip instanceId validation
   * @param {boolean} options.skipStabilization - Skip stabilization delay
   * @returns {boolean} - True if state was updated, false if rejected
   */
  const updateState = useCallback((incomingState, options = {}) => {
    const {
      skipVersionCheck = false,
      skipValidation = false,
      skipStabilization = false
    } = options;
    
    // ✅ CRITICAL: Check state version to prevent accepting stale state updates
    if (!skipVersionCheck) {
      const currentVersion = lastStateVersionRef.current || (gameStateRef.current?.stateVersion || 0);
      const incomingVersion = incomingState.stateVersion || 0;
      
      if (incomingVersion < currentVersion) {
        // Reject stale state - it's older than current state
        console.warn(`⚠️ Rejected stale state update: incoming=${incomingVersion}, current=${currentVersion}. Requesting fresh state...`);
        return false; // State rejected
      }
    }
    
    // ✅ IMPROVEMENT: Validate instanceIds BEFORE updating state
    if (!skipValidation) {
      try {
        validateGameStateInstanceIds(incomingState, userId);
      } catch (error) {
        console.error('🚨 STATE VALIDATION FAILED: InstanceIds missing in incoming state!', error);
        console.error('🚨 This indicates a backend bug - instanceIds should never be missing');
        // Still accept the state (backend is authoritative) but log the error
      }
    }
    
    // ✅ IMPROVEMENT: Detect instanceId changes by comparing with current state
    if (gameStateRef.current) {
      detectInstanceIdChanges(gameStateRef.current, incomingState, userId);
    }
    
    // ✅ IMPROVEMENT: Update state version tracking AFTER validation but BEFORE state update
    if (incomingState.stateVersion) {
      lastStateVersionRef.current = incomingState.stateVersion;
    }
    
    // ✅ ENHANCED DESYNC FIX: Update ref first, then state, then trigger stabilization
    gameStateRef.current = incomingState;
    
    // Call React state update callback
    if (onStateUpdate) {
      onStateUpdate(incomingState);
    }
    
    // ✅ ENHANCED DESYNC FIX: Trigger state stabilization delay
    if (!skipStabilization) {
      setIsStateStabilizing(true);
      if (stateStabilizationTimer.current) {
        clearTimeout(stateStabilizationTimer.current);
      }
      stateStabilizationTimer.current = setTimeout(() => {
        setIsStateStabilizing(false);
        console.log('✅ State stabilized - actions now allowed');
      }, STATE_STABILIZATION_DELAY_MS);
    }
    
    return true; // State updated successfully
  }, [userId, onStateUpdate, detectInstanceIdChanges]);
  
  /**
   * Gets the current game state (from ref, always latest)
   * @returns {Object|null} - Current game state or null
   */
  const getCurrentState = useCallback(() => {
    return gameStateRef.current;
  }, []);
  
  /**
   * Validates state before performing an action
   * @param {Object} state - State to validate (defaults to current state)
   * @returns {boolean} - True if valid
   */
  const validateState = useCallback((state = null) => {
    const stateToValidate = state || gameStateRef.current;
    if (!stateToValidate) return false;
    
    try {
      validateGameStateInstanceIds(stateToValidate, userId);
      return true;
    } catch (error) {
      console.error('🚨 State validation failed:', error);
      return false;
    }
  }, [userId]);
  
  /**
   * Unlocks UI after receiving state update or on error
   */
  const unlockUI = useCallback(() => {
    setIsStateUpdating(false);
    setShowLoadingOverlay(false);
    if (stateUpdateTimeout.current) {
      clearTimeout(stateUpdateTimeout.current);
      stateUpdateTimeout.current = null;
    }
    if (loadingOverlayTimeout.current) {
      clearTimeout(loadingOverlayTimeout.current);
      loadingOverlayTimeout.current = null;
    }
  }, []);
  
  /**
   * Locks UI when sending action to backend
   * Shows loading overlay after delay if action takes too long
   * @param {Function} onTimeout - Optional callback when timeout occurs (for error messages)
   * @param {Function} onTimeoutRequestRefresh - Optional callback to request state refresh on timeout
   */
  const lockUI = useCallback((onTimeout = null, onTimeoutRequestRefresh = null) => {
    setIsStateUpdating(true);
    
    // Show overlay only if action takes longer than 300ms (delayed feedback)
    if (loadingOverlayTimeout.current) {
      clearTimeout(loadingOverlayTimeout.current);
    }
    loadingOverlayTimeout.current = setTimeout(() => {
      setShowLoadingOverlay(true);
    }, 300); // Show overlay after 300ms delay
    
    // Set timeout to unlock UI if backend doesn't respond
    if (stateUpdateTimeout.current) {
      clearTimeout(stateUpdateTimeout.current);
    }
    stateUpdateTimeout.current = setTimeout(() => {
      console.error('⏱️ State update timeout - unlocking UI');
      unlockUI();
      if (onTimeout) {
        onTimeout();
      }
      if (onTimeoutRequestRefresh) {
        onTimeoutRequestRefresh();
      }
    }, 5000); // 5 second timeout
  }, [unlockUI]);
  
  /**
   * Gets the current state version
   * @returns {number} - Current state version or 0
   */
  const getStateVersion = useCallback(() => {
    return lastStateVersionRef.current || (gameStateRef.current?.stateVersion || 0);
  }, []);
  
  /**
   * Quick version check - validates if incoming state version is acceptable
   * This is a lightweight check that can be done early, before full state processing
   * @param {Object} incomingState - Incoming game state (must have stateVersion property)
   * @returns {boolean} - True if version is acceptable (newer or equal), false if stale
   */
  const checkStateVersion = useCallback((incomingState) => {
    if (!incomingState || typeof incomingState.stateVersion === 'undefined') {
      // If no version provided, accept it (might be from old backend or special message)
      return true;
    }
    
    const currentVersion = lastStateVersionRef.current || (gameStateRef.current?.stateVersion || 0);
    const incomingVersion = incomingState.stateVersion || 0;
    
    return incomingVersion >= currentVersion;
  }, []);
  
  /**
   * Cleanup function for timers
   */
  const cleanup = useCallback(() => {
    if (stateStabilizationTimer.current) {
      clearTimeout(stateStabilizationTimer.current);
      stateStabilizationTimer.current = null;
    }
    if (stateUpdateTimeout.current) {
      clearTimeout(stateUpdateTimeout.current);
      stateUpdateTimeout.current = null;
    }
    if (loadingOverlayTimeout.current) {
      clearTimeout(loadingOverlayTimeout.current);
      loadingOverlayTimeout.current = null;
    }
  }, []);
  
  return {
    // State access
    gameStateRef,
    getCurrentState,
    getStateVersion,
    
    // State flags
    isStateStabilizing,
    isStateUpdating,
    showLoadingOverlay,
    
    // State management functions
    updateState,
    validateState,
    checkStateVersion, // ✅ IMPROVEMENT #1: Early version check
    lockUI,
    unlockUI,
    cleanup,
    
    // Direct access to refs (for compatibility)
    lastStateVersionRef
  };
};

